import { useCallback, useMemo, useState } from 'react';
import { type WorkQueueBucket } from '@aps/contracts';

/**
 * Yo'naltirish.
 *
 * Router kutubxonasi ATAYLAB olinmagan: ekranlar orasidagi o'tish
 * chiziqli (menyu -> ekran -> ichkariga -> orqaga) va URL ulashish
 * talabi yo'q — admin panel Telegramdagi havoladan emas, xodimning
 * brauzeridan ochiladi. Kutubxona ~12 KB qo'shardi.
 *
 * Ildiz ekranlar `RootName` da — ular yon menyudan tanlanadi va
 * stackni TOZALAYDI. `bucket`, `order`, `product` esa ildiz emas:
 * ularga ichkaridan kiriladi va "orqaga" ishlashi kerak.
 */

/**
 * Yon menyudan ochiladigan ekranlar.
 *
 * Ro'yxat bir joyda: `switchRoot` ham, `Sidebar` ham shu tipdan
 * oziqlanadi. Ikki joyda yozilsa menyuga qo'shilgan punkt
 * `switchRoot` ga tushmay qolardi va `tsc` buni ushlamasdi.
 */
export type RootName =
  | 'dashboard'
  | 'queue'
  | 'orders'
  | 'payments'
  | 'consignments'
  | 'products'
  | 'categories'
  | 'import'
  | 'searchLog'
  | 'garageStats'
  | 'regionStats'
  | 'shops'
  | 'zones'
  | 'users'
  | 'statuses'
  | 'reference'
  | 'deliveries'
  | 'audit';
export type Route =
  | { name: 'queue' }
  | { name: 'bucket'; bucket: WorkQueueBucket }
  | { name: 'orders' }
  | { name: 'order'; orderId: string }
  | { name: 'payments' }
  | { name: 'products' }
  /** `productId: null` — yangi tovar formasi. */
  | { name: 'product'; productId: string | null }
  | { name: 'import' }
  | { name: 'reference' }
  | { name: 'deliveries' }
  | { name: 'audit' }
  | { name: 'users' }
  | { name: 'dashboard' }
  | { name: 'consignments' }
  | { name: 'categories' }
  | { name: 'shops' }
  | { name: 'zones' }
  | { name: 'statuses' }
  | { name: 'searchLog' }
  | { name: 'garageStats' }
  | { name: 'regionStats' };

/**
 * Kirgandan keyingi BIRINCHI ekran.
 *
 * Boshqaruv paneli, ish navbati EMAS: xodim kirganda avval "bugun
 * nima muhim" ni ko'radi, keyin ishga tushadi. Navbat bir bosishda —
 * signaldan to'g'ridan-to'g'ri o'sha yerga o'tiladi.
 */
const ROOT: Route = { name: 'dashboard' };

export interface Navigation {
  route: Route;
  openBucket: (bucket: WorkQueueBucket) => void;
  openOrder: (orderId: string) => void;
  openProduct: (productId: string | null) => void;
  /**
   * Joriy ekranni tovar ekrani bilan ALMASHTIRADI (stekka qo'shmaydi).
   * Yaratilgandan keyin "orqaga" bo'sh formaga qaytarmasligi uchun.
   */
  replaceProduct: (productId: string) => void;
  /** Ildizni almashtiradi — stack tozalanadi, "orqaga" tarixi qolmaydi. */
  switchRoot: (name: RootName) => void;
  goBack: () => void;
  canGoBack: boolean;
}

export function useNavigation(initial: RootName = 'dashboard'): Navigation {
  const [stack, setStack] = useState<Route[]>([{ name: initial }]);

  const openBucket = useCallback((bucket: WorkQueueBucket) => {
    setStack((current) => [...current, { name: 'bucket', bucket }]);
  }, []);

  const openOrder = useCallback((orderId: string) => {
    setStack((current) => [...current, { name: 'order', orderId }]);
  }, []);

  const openProduct = useCallback((productId: string | null) => {
    setStack((current) => [...current, { name: 'product', productId }]);
  }, []);

  const replaceProduct = useCallback((productId: string) => {
    setStack((current) => [...current.slice(0, -1), { name: 'product', productId }]);
  }, []);

  const switchRoot = useCallback((name: RootName) => {
    setStack([{ name }]);
  }, []);

  const goBack = useCallback(() => {
    // Oxirgi ekran hech qachon olib tashlanmaydi — bo'sh stack UI ni yiqitadi.
    setStack((current) => (current.length <= 1 ? current : current.slice(0, -1)));
  }, []);

  return useMemo(
    () => ({
      route: stack.at(-1) ?? ROOT,
      openBucket,
      openOrder,
      openProduct,
      replaceProduct,
      switchRoot,
      goBack,
      canGoBack: stack.length > 1,
    }),
    [stack, openBucket, openOrder, openProduct, replaceProduct, switchRoot, goBack],
  );
}
